import { useSchoolTeachersQuery } from '@innovamat/glow-api-client';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import type { OrganizationTeacher } from '../modules/teacher';

type Variables = {
  organizationId: string;
  isPlatformEnabled: boolean;
};

type UseSchoolTeachersReturn = {
  data: OrganizationTeacher[];
  isLoading: boolean;
  queryKey: unknown[];
  refetch: () => void;
  invalidate: () => void;
};

type SchoolTeachersQuery = {
  teachers?: OrganizationTeacher[];
};

const getSchoolTeachersQueryKey = (variables: Variables): unknown[] =>
  useSchoolTeachersQuery.getKey(variables);

const refetchSchoolTeachers = (
  queryClient: QueryClient,
  variables: Variables
): void => {
  queryClient.refetchQueries({
    queryKey: getSchoolTeachersQueryKey(variables),
  });
};

const invalidateSchoolTeachers = (
  queryClient: QueryClient,
  variables: Variables
): void => {
  queryClient.invalidateQueries({
    queryKey: getSchoolTeachersQueryKey(variables),
  });
};

function useSchoolTeachers(variables: Variables): UseSchoolTeachersReturn {
  const queryClient = useQueryClient();
  const { data, isLoading } =
    useSchoolTeachersQuery<SchoolTeachersQuery>(variables);

  return {
    data: data?.teachers || [],
    isLoading,
    queryKey: getSchoolTeachersQueryKey(variables),
    refetch: () => refetchSchoolTeachers(queryClient, variables),
    invalidate: () => invalidateSchoolTeachers(queryClient, variables),
  };
}

export {
  getSchoolTeachersQueryKey,
  invalidateSchoolTeachers,
  refetchSchoolTeachers,
  useSchoolTeachers,
};
export type { Variables as UseSchoolTeachersVariables };
